import { createContext, useCallback, useContext, useState } from "react";
import { UserDataContext } from "./Contexts";
import { getNotificationsService } from "../../infrastructure/services/NotificationsService";

const NotificationsFeedContext = createContext(null);
const notificationsPageSize = 50;

const hasUnreadNotification = (notification) => {
  if (typeof notification?.seen === "boolean") {
    return notification.seen === false;
  }

  if (typeof notification?.seen === "string") {
    return notification.seen.toLowerCase() === "false";
  }

  if (typeof notification?.visto === "boolean") {
    return notification.visto === false;
  }

  if (typeof notification?.visto === "string") {
    return notification.visto.toLowerCase() === "false";
  }

  return false;
};

export const useNotificationsFeedState = () => {
  const { userData } = useContext(UserDataContext);
  const [notificationsList, setNotificationsList] = useState([]);
  const [showBellDot, setShowBellDot] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  const transformNotifications = useCallback((notifications = []) => {
    const normalizedNotifications = Array.isArray(notifications)
      ? notifications
      : [];

    setNotificationsList(normalizedNotifications);
    const unreadCount = normalizedNotifications.filter(hasUnreadNotification).length;

    setUnreadNotificationsCount(unreadCount);
    setShowBellDot(unreadCount > 0);
  }, []);

  const refreshNotifications = useCallback(async ({ force = false } = {}) => {
    if (!userData?.idUsuario) {
      transformNotifications([]);
      return [];
    }

    try {
      const notifications = await getNotificationsService(userData.idUsuario, {
        page: 1,
        pageSize: notificationsPageSize,
        visto: false,
        forceRefresh: force,
      });
      transformNotifications(notifications);
      return notifications;
    } catch (error) {
      console.error("Failed getting notifications", error);
      return [];
    }
  }, [transformNotifications, userData?.idUsuario]);

  const resetNotificationsFeed = useCallback(() => {
    transformNotifications([]);
  }, [transformNotifications]);

  return {
    notificationsList,
    showBellDot,
    unreadNotificationsCount,
    refreshNotifications,
    resetNotificationsFeed,
  };
};

export const NotificationsFeedProvider = ({ value, children }) => {
  return (
    <NotificationsFeedContext.Provider value={value}>
      {children}
    </NotificationsFeedContext.Provider>
  );
};

export const useNotificationsFeed = () => {
  const context = useContext(NotificationsFeedContext);

  if (!context) {
    throw new Error(
      "useNotificationsFeed must be used within a NotificationsFeedProvider",
    );
  }

  return context;
};
